import ChannelCards from "@/components/channels/ChannelCards";
import ChannelIntegrationCards from "@/components/channels/ChannelIntegrationCards";
import ChannelTypeSelect from "@/components/channels/ChannelTypeSelect";
import DeleteConfirmDialog from "@/components/channels/DeleteConfirmDialog";
import SlackChannelForm from "@/components/channels/SlackChannelForm";
import PaginationControl from "@/components/common/PaginationControl";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { channelService } from "@/services/channel.service/channel.api";
import { Channel } from "@/types/channel";
import { MessageSquare, MessagesSquare, PlusCircle, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Channels() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isTypeSelectOpen, setIsTypeSelectOpen] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<"slack">("slack");
  const [currentChannel, setCurrentChannel] = useState<Channel>({
    _id: "",
    name: "",
    isActive: true,
    type: "slack",
    createdAt: "",
    updatedAt: ""
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchChannels = useCallback(async () => {
    setIsLoading(true);
    const searchParams = {
      page,
      limit,
      ...(query.trim() ? { search: query.trim() } : {})
    };

    const [res, error] = await channelService.getAll(searchParams);

    if (error) {
      console.error("Erro ao buscar canais:", error);
      return;
    }

    if (res) {
      if (Array.isArray(res.result)) {
        setChannels(res.result);
      } else {
        setChannels([]);
      }
      setTotalPages(res.qtdPages);
      setTotalItems(res.total);
    }
    setIsLoading(false);
  }, [query, page, limit]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const handleCreateChannel = async (data: { name: string; slackKey?: string; slackPrivateKey?: string; knowledgeBaseIds?: string[] }) => {
    try {
      const [res] = await channelService.create({
        name: data.name,
        type: "slack",
        slackKey: data.slackKey,
        slackPrivateKey: data.slackPrivateKey,
        knowledgeBaseIds: data.knowledgeBaseIds || [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      if (res?._id) {
        await fetchChannels();
        setIsCreateDialogOpen(false);
        toast.success("Canal criado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao criar canal:", error);
      toast.error("Erro ao criar canal");
    }
  };

  const handleUpdateChannel = async (channel: Channel) => {
    try {
      const updateData = {
        name: channel.name,
        knowledgeBaseIds: channel.knowledgeBaseIds,
        slackKey: channel.slackKey,
        slackPrivateKey: channel.slackPrivateKey
      };
      await channelService.update(channel._id, updateData);
      await fetchChannels();
      setIsEditDialogOpen(false);
      toast.success("Canal atualizado com sucesso!");
    } catch (error) {
      console.error("Erro ao atualizar canal:", error);
      toast.error("Erro ao atualizar canal");
    }
  };

  const handleDeleteChannel = async (channelId: string) => {
    if (!channelId) {
      toast.error("ID do canal não encontrado");
      return;
    }
    try {
      await channelService.delete(channelId);
      fetchChannels();
      setIsDeleteDialogOpen(false);
      toast.success("Canal removido com sucesso!");
    } catch (error) {
      console.error("Erro ao remover canal:", error);
      toast.error("Erro ao remover canal");
    }
  };

  const handleUpdateChannelStatus = async (channelId: string, isActive: boolean) => {
    try {
      await channelService.update(channelId, { isActive } as Partial<Channel>);
      fetchChannels();
    } catch (error) {
      console.error("Erro ao atualizar status do canal:", error);
    }
  };

  const handleEditClick = (channel: Channel) => {
    setCurrentChannel(channel);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (channel: Channel) => {
    setCurrentChannel(channel);
    setIsDeleteDialogOpen(true);
  };

  const handleChannelTypeSelect = (type: "slack") => {
    setSelectedChannelType(type);
    setIsTypeSelectOpen(false);
    setIsCreateDialogOpen(true);
  };

  const handleConfigureIntegration = (type: string) => {
    if (type === "whatsapp") {
      navigate("/whatsapp-settings");
      return;
    }
    if (type === "slack") {
      setSelectedChannelType("slack");
      setIsCreateDialogOpen(true);
    }
  };

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };
  const handleSearch = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  return (
    <>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Canais de Atendimento</h1>
          <p className="mt-1 text-muted-foreground">
            Configure e gerencie de onde vêm as conversas com seus clientes.
          </p>
        </div>
        <Drawer open={isTypeSelectOpen} onOpenChange={setIsTypeSelectOpen}>
          <DrawerTrigger asChild>
            <Button className="gap-2 shrink-0">
              <PlusCircle className="h-4 w-4" />
              Novo Canal
            </Button>
          </DrawerTrigger>
          <DrawerContent>
            <ChannelTypeSelect
              onSelect={handleChannelTypeSelect}
              onClose={() => setIsTypeSelectOpen(false)}
            />
          </DrawerContent>
        </Drawer>
      </div>

      {/* Integrações disponíveis */}
      <div className="mb-8">
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Integrações
        </h2>
        <ChannelIntegrationCards
          integrations={[
            {
              type: "whatsapp",
              name: "WhatsApp",
              description: "Atenda seus clientes pela WhatsApp Cloud API.",
              icon: MessagesSquare,
            },
            {
              type: "slack",
              name: "Slack",
              description: "Conecte canais do Slack ao atendimento.",
              icon: MessageSquare,
              count: totalItems,
            },
          ]}
          onConfigure={handleConfigureIntegration}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Canais configurados
        </h2>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar canais..."
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl border border-border/60 bg-card" />
          ))}
        </div>
      ) : (
        <ChannelCards
          channels={channels}
          onEdit={handleEditClick}
          onToggleStatus={handleUpdateChannelStatus}
          onGenerateQRCode={() => {}}
        />
      )}

      <div className="mt-6">
        <PaginationControl
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      <Drawer open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Novo Canal</DrawerTitle>
            <DrawerDescription>
              Configure seu canal de atendimento (Slack)
            </DrawerDescription>
          </DrawerHeader>
          {selectedChannelType === "slack" && (
            <SlackChannelForm
              onClose={() => {
                setIsCreateDialogOpen(false);
                fetchChannels();
              }}
              onSave={handleCreateChannel}
            />
          )}
        </DrawerContent>
      </Drawer>

      <Drawer open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Editar Canal</DrawerTitle>
            <DrawerDescription>
              Atualize as configurações do canal
            </DrawerDescription>
          </DrawerHeader>
          {currentChannel.type === "slack" && (
            <SlackChannelForm
              initialName={currentChannel.name}
              initialSlackKey={currentChannel.slackKey}
              initialSlackPrivateKey={currentChannel.slackPrivateKey}
              initialKnowledgeBaseIds={currentChannel.knowledgeBaseIds}
              onClose={() => setIsEditDialogOpen(false)}
              onSave={(data) => handleUpdateChannel({
                ...currentChannel,
                name: data.name,
                slackKey: data.slackKey,
                slackPrivateKey: data.slackPrivateKey,
                knowledgeBaseIds: data.knowledgeBaseIds
              })}
            />
          )}
        </DrawerContent>
      </Drawer>

      <Drawer open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DrawerContent>
          <DeleteConfirmDialog
            onCancel={() => setIsDeleteDialogOpen(false)}
            onConfirm={() => handleDeleteChannel(currentChannel._id)}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}
